import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWallet } from "../lib/wallet";
import { placeBid } from "../lib/api";
import { X, Gavel } from "lucide-react";
import { toast } from "sonner";

export default function BidModal({ open, onClose, auction, token, onSuccess }) {
    const { t } = useTranslation();
    const { account, connect } = useWallet();
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);

    if (!open || !auction || !token) return null;
    const min = auction.current_bid + 1;

    const submit = async (e) => {
        e.preventDefault();
        if (!account) { await connect(); return; }
        const a = parseFloat(amount);
        if (a <= auction.current_bid) {
            toast.error(t("bid.must_exceed", { n: auction.current_bid }));
            return;
        }
        setBusy(true);
        try {
            await placeBid(token.number, account, a);
            toast.success(t("bid.placed", { n: a, id: token.number }));
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to place bid");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-obsidian/80 backdrop-blur-sm">
            <form onSubmit={submit} className="relative w-full max-w-md bg-slate-950 border border-gold" data-testid="bid-modal">
                <button type="button" data-testid="bid-modal-close" onClick={onClose}
                    className="absolute top-3 right-3 text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                </button>
                <div className="p-6 border-b border-white/10">
                    <div className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                        <Gavel className="w-3 h-3" /> {t("bid.kicker")}
                    </div>
                    <div className="font-serif text-3xl mt-2">{t("bid.title", { n: token.number })}</div>
                    <div className="font-mono text-xs text-slate-400 mt-1">
                        {t("bid.current_bid_label")} <span className="text-gold">{auction.current_bid.toLocaleString()} USDC</span>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <label className="block">
                        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
                            {t("bid.your_bid")}
                        </span>
                        <input
                            data-testid="bid-amount-input"
                            type="number"
                            step="0.01"
                            min={min}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder={t("bid.min", { n: min })}
                            className="mt-2 w-full bg-slate-900 border border-white/10 px-3 py-3 font-mono focus:border-gold focus:outline-none"
                            required
                        />
                    </label>
                    <p className="text-[11px] text-slate-500 leading-relaxed">{t("bid.info")}</p>
                </div>
                <div className="p-6 border-t border-white/10 flex gap-3">
                    <button type="button" data-testid="bid-modal-cancel" onClick={onClose}
                        className="flex-1 btn-sharp border border-white/15 px-4 py-3 text-xs hover:border-white/40">
                        {t("bid.cancel")}
                    </button>
                    <button data-testid="bid-modal-submit" type="submit" disabled={busy}
                        className="flex-1 btn-sharp bg-gold text-obsidian px-4 py-3 text-xs hover:opacity-90 disabled:opacity-60">
                        {busy ? t("bid.placing") : account ? t("bid.submit") : t("bid.connect_and_bid")}
                    </button>
                </div>
            </form>
        </div>
    );
}
